import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const handler = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return reduced;
}

type Slide = {
  title: string;
  body: string;
  illustration: JSX.Element;
};

const TERRACOTTA = "#C1502E";
const GOLD = "#D4A017";
const FOREST = "#2D5016";
const CLAY = "#8B5E3C";
const CREAM = "#FAF6F1";
const INK = "#1C1917";

/* ----- Illustrations (inline SVG, Afro-modern) ----- */

const CommunityIllustration = (
  <svg viewBox="0 0 280 220" className="w-full max-w-[280px] h-auto" aria-hidden>
    <defs>
      <linearGradient id="ill-bubble" x1="0" x2="1" y1="0" y2="1">
        <stop offset="0%" stopColor={TERRACOTTA} />
        <stop offset="100%" stopColor={GOLD} />
      </linearGradient>
    </defs>
    {/* Backdrop ring */}
    <circle cx="140" cy="120" r="95" fill={CLAY} opacity="0.08" />
    <circle cx="140" cy="120" r="70" fill="none" stroke={TERRACOTTA} strokeWidth="1" strokeDasharray="2 6" opacity="0.5" />

    {/* Three figures with kente-collar accent */}
    {[
      { x: 80, color: FOREST },
      { x: 140, color: TERRACOTTA },
      { x: 200, color: GOLD },
    ].map((p, i) => (
      <g key={i} transform={`translate(${p.x - 24}, 100)`}>
        <circle cx="24" cy="22" r="18" fill={p.color} />
        {/* Kente collar */}
        <path d="M4 50 Q24 38 44 50 L44 64 L4 64 Z" fill={p.color} />
        <g stroke={CREAM} strokeWidth="1.6">
          <line x1="8" y1="55" x2="40" y2="55" />
          <line x1="14" y1="60" x2="34" y2="60" />
        </g>
        <path d="M4 50 Q24 38 44 50 L44 64 L4 64 Z" fill="none" stroke={INK} strokeWidth="0.8" opacity="0.3" />
      </g>
    ))}

    {/* Floating chat bubbles */}
    <g transform="translate(40 60)">
      <rect width="44" height="26" rx="10" fill="url(#ill-bubble)" />
      <circle cx="14" cy="13" r="2" fill={CREAM} />
      <circle cx="22" cy="13" r="2" fill={CREAM} />
      <circle cx="30" cy="13" r="2" fill={CREAM} />
    </g>
    <g transform="translate(196 40)">
      <rect width="44" height="26" rx="10" fill={CREAM} stroke={TERRACOTTA} strokeWidth="1.5" />
      <circle cx="14" cy="13" r="2" fill={TERRACOTTA} />
      <circle cx="22" cy="13" r="2" fill={TERRACOTTA} />
      <circle cx="30" cy="13" r="2" fill={TERRACOTTA} />
    </g>

    {/* Sparkle */}
    <g transform="translate(60 30)" fill={GOLD}>
      <path d="M0 6 L4 4 L6 0 L8 4 L12 6 L8 8 L6 12 L4 8 Z" />
    </g>
  </svg>
);

const StoriesIllustration = (
  <svg viewBox="0 0 280 220" className="w-full max-w-[280px] h-auto" aria-hidden>
    <defs>
      <linearGradient id="ill-ring" x1="0" x2="1" y1="0" y2="1">
        <stop offset="0%" stopColor={TERRACOTTA} />
        <stop offset="50%" stopColor={GOLD} />
        <stop offset="100%" stopColor={FOREST} />
      </linearGradient>
    </defs>
    <circle cx="140" cy="120" r="95" fill={GOLD} opacity="0.06" />

    {/* Big story ring */}
    <g transform="translate(140 110)">
      <circle r="62" fill="none" stroke="url(#ill-ring)" strokeWidth="6" />
      <circle r="50" fill={CREAM} stroke={INK} strokeOpacity="0.08" />
      {/* Adinkra sun */}
      <g fill={TERRACOTTA}>
        <circle r="14" />
      </g>
      <g stroke={GOLD} strokeWidth="3" strokeLinecap="round">
        {Array.from({ length: 8 }).map((_, i) => {
          const a = (i * Math.PI) / 4;
          const x1 = Math.cos(a) * 22;
          const y1 = Math.sin(a) * 22;
          const x2 = Math.cos(a) * 32;
          const y2 = Math.sin(a) * 32;
          return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} />;
        })}
      </g>
    </g>

    {/* Mini rings */}
    {[40, 240].map((x, i) => (
      <g key={i} transform={`translate(${x} 60)`}>
        <circle r="20" fill="none" stroke="url(#ill-ring)" strokeWidth="3" />
        <circle r="14" fill={i ? GOLD : FOREST} />
      </g>
    ))}

    {/* Sparkles */}
    <g fill={GOLD}>
      <path d="M60 170 l3 -2 l2 -4 l2 4 l3 2 l-3 2 l-2 4 l-2 -4 z" />
      <path d="M210 170 l3 -2 l2 -4 l2 4 l3 2 l-3 2 l-2 4 l-2 -4 z" />
    </g>
  </svg>
);

const ShieldIllustration = (
  <svg viewBox="0 0 280 220" className="w-full max-w-[280px] h-auto" aria-hidden>
    <defs>
      <linearGradient id="ill-shield" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0%" stopColor={TERRACOTTA} />
        <stop offset="100%" stopColor={CLAY} />
      </linearGradient>
    </defs>
    <circle cx="140" cy="120" r="95" fill={FOREST} opacity="0.06" />

    {/* Shield */}
    <g transform="translate(140 110)">
      <path
        d="M0 -56 L46 -36 L46 8 C46 36 24 58 0 64 C-24 58 -46 36 -46 8 L-46 -36 Z"
        fill="url(#ill-shield)"
      />
      {/* Kente diamond grid overlay */}
      <g opacity="0.25" stroke={CREAM} strokeWidth="1">
        {Array.from({ length: 6 }).map((_, r) =>
          Array.from({ length: 6 }).map((__, c) => (
            <rect
              key={`${r}-${c}`}
              x={-30 + c * 12}
              y={-30 + r * 12}
              width="8"
              height="8"
              transform={`rotate(45 ${-26 + c * 12} ${-26 + r * 12})`}
            />
          )),
        )}
      </g>
      {/* Checkmark */}
      <path d="M-14 4 L-4 14 L18 -10" fill="none" stroke={CREAM} strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
    </g>

    {/* Speed lines */}
    <g stroke={GOLD} strokeWidth="3" strokeLinecap="round">
      <line x1="40" y1="100" x2="64" y2="100" />
      <line x1="32" y1="116" x2="58" y2="116" />
      <line x1="40" y1="132" x2="64" y2="132" />
    </g>
    <g stroke={FOREST} strokeWidth="3" strokeLinecap="round" opacity="0.7">
      <line x1="216" y1="100" x2="240" y2="100" />
      <line x1="222" y1="116" x2="248" y2="116" />
      <line x1="216" y1="132" x2="240" y2="132" />
    </g>
  </svg>
);

const SLIDES: Slide[] = [
  {
    title: "Your tribe, your voice — everywhere",
    body: "Bring your people together. Group chats, direct messages and shared moments, all in one warm place.",
    illustration: CommunityIllustration,
  },
  {
    title: "Stories that spark connection",
    body: "Share what's happening in 24-hour stories. Reactions, replies, and ring-glow status updates that feel alive.",
    illustration: StoriesIllustration,
  },
  {
    title: "Safe, fast, beautifully African",
    body: "Private by default. Block, mute, and control who sees you. Designed and tuned for African networks.",
    illustration: ShieldIllustration,
  },
];

export function ChitChatOnboardingCarousel({
  initialSlide = 0,
  onSlideChange,
  onDone,
}: {
  initialSlide?: number;
  onSlideChange?: (slideIndex: number) => void;
  onDone: () => void;
}) {
  const [idx, setIdx] = useState(initialSlide);
  const last = idx === SLIDES.length - 1;
  const slide = SLIDES[idx];
  const reducedMotion = usePrefersReducedMotion();

  const nextBtnRef = useRef<HTMLButtonElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  const goTo = (target: number) => {
    const clamped = Math.max(0, Math.min(SLIDES.length - 1, target));
    if (clamped === idx) return;
    setIdx(clamped);
    onSlideChange?.(clamped);
  };

  const next = () => {
    if (last) {
      onDone();
    } else {
      goTo(idx + 1);
    }
  };

  const prev = () => {
    if (idx > 0) goTo(idx - 1);
  };

  // Focus the primary action when the dialog mounts so keyboard / SR users
  // can advance immediately. Move focus to the heading on each slide change
  // so screen readers announce the new slide title.
  useEffect(() => {
    nextBtnRef.current?.focus();
  }, []);

  useEffect(() => {
    headingRef.current?.focus();
  }, [idx]);

  // Keyboard support: arrows to navigate, Esc to skip, Home/End to jump.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") {
        e.preventDefault();
        next();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        prev();
      } else if (e.key === "Home") {
        e.preventDefault();
        goTo(0);
      } else if (e.key === "End") {
        e.preventDefault();
        goTo(SLIDES.length - 1);
      } else if (e.key === "Escape") {
        e.preventDefault();
        onDone();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, last]);

  const totalSlides = SLIDES.length;

  return (
    <div
      ref={dialogRef}
      className={cn(
        "chitchat-theme fixed inset-0 z-[90] flex flex-col",
        reducedMotion && "motion-reduce",
      )}
      style={{ background: CREAM }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="cc-onboarding-title"
      aria-describedby="cc-onboarding-body"
    >
      {/* Progress dots — exposed as a tablist for assistive tech */}
      <div
        className="flex items-center justify-center gap-2 pt-10"
        role="tablist"
        aria-label="Onboarding progress"
      >
        {SLIDES.map((s, i) => (
          <button
            key={i}
            type="button"
            role="tab"
            aria-selected={i === idx}
            aria-label={`Slide ${i + 1} of ${totalSlides}: ${s.title}`}
            tabIndex={i === idx ? 0 : -1}
            onClick={() => goTo(i)}
            className={cn(
              "h-2 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
              !reducedMotion && "transition-all",
              i === idx ? "w-8" : "w-2",
            )}
            style={{
              background: i <= idx ? TERRACOTTA : "#EDE8E3",
            }}
          />
        ))}
      </div>

      {/* Live region announces slide changes */}
      <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        Slide {idx + 1} of {totalSlides}: {slide.title}. {slide.body}
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-8 text-center gap-6">
        <div className="flex items-center justify-center w-full" aria-hidden="true">
          {slide.illustration}
        </div>
        <h2
          ref={headingRef}
          id="cc-onboarding-title"
          tabIndex={-1}
          className="font-display text-2xl font-extrabold leading-tight max-w-[300px] focus:outline-none"
          style={{ color: INK }}
        >
          {slide.title}
        </h2>
        <p
          id="cc-onboarding-body"
          className="font-body text-sm leading-relaxed max-w-[320px]"
          style={{ color: "#8A817A" }}
        >
          {slide.body}
        </p>
      </div>

      {/* Actions */}
      <div className="px-6 pb-10 flex flex-col gap-3 items-center">
        <Button
          ref={nextBtnRef}
          onClick={next}
          aria-label={last ? "Finish onboarding and get started" : `Continue to slide ${idx + 2} of ${totalSlides}`}
          className="w-full max-w-[320px] h-12 font-display font-bold text-base rounded-[14px] shadow-md hover:opacity-90 focus-visible:ring-2 focus-visible:ring-offset-2"
          style={{ background: TERRACOTTA, color: "#FFFFFF" }}
        >
          {last ? "Get started" : "Next"}
        </Button>
        {!last && (
          <button
            type="button"
            onClick={onDone}
            aria-label="Skip onboarding and go to ChitChat"
            className="font-body text-sm font-medium hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 rounded px-2 py-1"
            style={{ color: "#8A817A" }}
          >
            Skip
          </button>
        )}
      </div>
    </div>
  );
}

